import { isFixedTile } from '@/challenges/editing';
import {
  compatibleBundleConnections,
  BUNDLE_COMPATIBILITY_MESSAGE,
  BUNDLE_COMPATIBILITY_BUDGET_MESSAGE,
} from '@/models/logic/BundleCompatibility';
import { analyzeBundleCompatibility } from '@/models/logic/ProspectiveContainment';
import {
  autorun,
  computed,
  isComputedProp,
  isObservableArray,
  isObservableProp,
  isObservable,
  makeAutoObservable,
  observable,
  runInAction,
  type IComputedValue,
  type IReactionDisposer,
} from 'mobx';
import type {
  ITileManagerModel,
  IConnectionModel,
  ConnectionModelConnectOptions,
  IChipInstanceModel,
} from '@/interfaces/models';
import { TileType } from '@/models/TileTypes';
import { Side } from '@/schemas/tiles';
import type { ChipInstanceId, TileId } from '@/types/ids';
import {
  ConnectionGraph,
  type Connection,
  type ConnectionGraphDto,
  type ConnectionPoint,
  type ConnectedNeighbor,
} from '@/models/connections/ConnectionGraph';
import { ConnectionCalculator } from '@/models/connections/ConnectionCalculator';
import { ConnectionTileTracker } from '@/models/connections/ConnectionTileTracker';
import { TileInstance, isConstructedTileInstance } from '@/models/tiles/TileInstance';
import { isConstructedTileMeta } from '@/models/tiles/SignalSides';
import { isConstructedTileCollection } from '@/models/TileManagerModel';
import { analyzeProspectiveContainment } from '@/models/logic/ProspectiveContainment';
import { resolveConnectionSides } from '@/models/logic/utils';
import { ChangeJournal } from '@/utils/ChangeJournal';

interface ConnectionModelOptions {
  tileManagerModel: ITileManagerModel;
  connectionCalculator: ConnectionCalculator;
  connectionDto?: ConnectionGraphDto;
}

const collectContainmentOwners = (tiles: TileInstance[]): ReadonlySet<string | null> =>
  new Set(tiles.map((tile) => tile.meta.partOfChip).filter(Boolean));

export class ConnectionModel implements IConnectionModel {
  private readonly connectionGraph: ConnectionGraph;
  private readonly connectionCalculator: ConnectionCalculator;
  private tileTracker?: ConnectionTileTracker;
  private containmentOwnerCache?: IComputedValue<ReadonlySet<string | null> | undefined>;
  private containmentOwnerDisposer?: () => void;
  private compatibilityDisposer?: IReactionDisposer;
  private containmentOwnerCacheDisabled = false;
  private readonly disconnected = new Map<string, Connection>();
  private readonly manualConnections = new Map<string, Connection>();
  private readonly recentMemberConnections = new Map<
    string,
    { edge: Connection; gesture: number }
  >();
  private tileByIdIndex = new Map<TileId, TileInstance>();
  private tileConnectionVersionMap = new Map<TileId, number>();
  private tileConnectionFlagsSnapshotMap = new Map<TileId, string>();
  /** Observable counter to invalidate MobX computed `dto` when connections change */
  private graphVersion = 0;
  private changes = new ChangeJournal<TileId>();
  private pendingConnectionBatch?: Array<{
    a: ConnectionPoint;
    b: ConnectionPoint;
    options?: ConnectionModelConnectOptions;
  }>;
  private approvedBatchScope?: NonNullable<
    ReturnType<ConnectionModel['containmentScope']>
  >;

  constructor(private readonly options: ConnectionModelOptions) {
    this.connectionGraph = new ConnectionGraph(options.connectionDto);
    this.connectionCalculator = options.connectionCalculator;
    this.rebuildTileIndex();
    for (const edge of options.connectionDto?.disconnected ?? []) {
      this.disconnected.set(this.makeConnectionKey(edge.a, edge.b), edge);
    }

    if (options.connectionDto) this.seedManualConnectionsFromGraph();
    // Saved edges supplement automatic forced connections, rather than replacing them.
    this.connectionCalculator.calculateConnections(
      options.tileManagerModel,
      this.connectionGraph,
    );
    this.reapplyManualConnections();

    this.applyDisconnections();
    this.rejectExpandingConnections(options.connectionDto?.connections ?? []);
    this.syncTileConnectionVersions();

    // Native hydration options are plain data records. Keep only the runtime
    // dependency after ingestion, so MobX does not wrap a second saved graph.
    // Preserve custom getters/prototypes and any changes made during ingestion.
    try {
      if (!isObservable(options) && Object.getPrototypeOf(options) === Object.prototype) {
        const descriptors = Object.getOwnPropertyDescriptors(options);
        if (
          Reflect.ownKeys(descriptors).every(
            (key) =>
              typeof key === 'string' &&
              ['tileManagerModel', 'connectionCalculator', 'connectionDto'].includes(
                key,
              ) &&
              'value' in descriptors[key],
          ) &&
          descriptors.tileManagerModel &&
          descriptors.connectionCalculator
        ) {
          this.options = {
            tileManagerModel: descriptors.tileManagerModel.value!,
            connectionCalculator: descriptors.connectionCalculator.value!,
          };
        }
      }
    } catch {
      // Custom proxies can reject inspection; retain their original behavior.
    }

    // These are lookup/comparison caches, not UI state. Rebuilding thousands
    // of observable Map entries on every edit adds no useful notifications.
    const nativeSelectors =
      hasNativeConnectionSelectorPrototype(this) &&
      !Object.getOwnPropertyDescriptor(this, 'connectionFlags') &&
      !Object.getOwnPropertyDescriptor(this, 'connectionVersionForTile');
    makeAutoObservable<
      this,
      | 'makeConnectionKey'
      | 'tileTracker'
      | 'containmentOwnerCache'
      | 'containmentOwnerDisposer'
      | 'compatibilityDisposer'
      | 'containmentOwnerCacheDisabled'
      | 'tileByIdIndex'
      | 'tileConnectionFlagsSnapshotMap'
      | 'manualConnections'
      | 'recentMemberConnections'
      | 'pendingConnectionBatch'
      | 'approvedBatchScope'
      | 'changes'
    >(
      this,
      {
        dispose: false,
        // Pure lookup called for every edge; avoid per-call MobX action allocation.
        makeConnectionKey: false,
        tileTracker: false,
        containmentOwnerCache: false,
        containmentOwnerDisposer: false,
        compatibilityDisposer: false,
        containmentOwnerCacheDisabled: false,
        tileByIdIndex: false,
        tileConnectionFlagsSnapshotMap: false,
        manualConnections: false,
        recentMemberConnections: false,
        pendingConnectionBatch: false,
        approvedBatchScope: false,
        changes: false,
      },
      { autoBind: true },
    );

    if (nativeSelectors && hasNativeConnectionSelectorPrototype(this)) {
      const flags = protectedSelectorDescriptor(this, 'connectionFlags');
      const version = protectedSelectorDescriptor(this, 'connectionVersionForTile');
      if (flags && version)
        nativeConnectionSelectors.set(this, {
          flags: flags.value,
          version: version.value,
        });
    }

    this.compatibilityDisposer = autorun(() => {
      void this.compatibleGraph;
    });

    this.tileTracker = new ConnectionTileTracker(
      this.options.tileManagerModel,
      this.tileByIdIndex,
      (affectedTileIds, removedTileIds) => {
        const recalculated = this.recalculateConnectionsForTileDelta(
          affectedTileIds,
          removedTileIds,
        );
        this.syncTileConnectionVersions(recalculated, removedTileIds);
      },
    );
  }

  dispose(): void {
    this.compatibilityDisposer?.();
    this.compatibilityDisposer = undefined;
    this.containmentOwnerDisposer?.();
    this.containmentOwnerDisposer = undefined;
    this.containmentOwnerCache = undefined;
    this.containmentOwnerCacheDisabled = true;
    this.tileTracker?.dispose();
    this.tileTracker = undefined;
  }

  get topologyVersion(): number {
    return this.graphVersion;
  }

  connectionChangesSince(version: number): ReadonlySet<TileId> | undefined {
    return this.tileTracker?.incremental ? this.changes.since(version) : undefined;
  }

  /** Admit healthy paste/stack connections together; invalid batches replay in their original order. */
  batchConnections<T>(run: () => T): T {
    if (this.pendingConnectionBatch) return run();
    const pending: NonNullable<ConnectionModel['pendingConnectionBatch']> = [];
    this.pendingConnectionBatch = pending;
    try {
      return run();
    } finally {
      this.pendingConnectionBatch = undefined;
      if (pending.length) {
        const scope = this.containmentScope();
        if (scope) {
          const current = this.connectionGraph.getAllConnections();
          const pendant =
            pending.every(({ options }) => !options?.tiles && !options?.tileManagers) &&
            this.pendantConnectionCertificate(scope.manager.tiles, current, pending);
          const after = pendant
            ? undefined
            : analyzeProspectiveContainment(
                scope.manager,
                [...current, ...pending],
                scope.instances,
              );
          const before =
            after && !after.budgetExceeded && after.cyclicRelationKeys.size
              ? analyzeProspectiveContainment(scope.manager, current, scope.instances)
              : undefined;
          if (
            pendant ||
            (after &&
              !after.budgetExceeded &&
              [...after.cyclicRelationKeys].every((key) =>
                before?.cyclicRelationKeys.has(key),
              ))
          )
            this.approvedBatchScope = scope;
        }
        try {
          for (const { a, b, options } of pending) this.connect(a, b, options);
        } finally {
          this.approvedBatchScope = undefined;
        }
      }
    }
  }

  connectionVersionForTile(tileId: TileId): number {
    return this.tileConnectionVersionMap.get(tileId) ?? 0;
  }

  private get compatibleGraph(): ConnectionGraph {
    void this.graphVersion;
    const context = this.tileByIdIndex.values().next().value?.context;
    if (!context?.userPreferences?.bundleChipIdentityEnabled) return this.connectionGraph;
    // Hydration publishes through the observable app context; the plain
    // context.isHydrating/project fields alone cannot invalidate this cache.
    void context.app?.context;
    const scope = this.containmentScope();
    if (!scope) return this.connectionGraph;
    return compatibleBundleConnections(
      scope.manager,
      { dto: this.connectionGraph.dto } as IConnectionModel,
      scope.instances,
    ).graph;
  }

  private acceptBundleCompatibility(connection: Connection): boolean {
    const context = this.tileByIdIndex.values().next().value?.context;
    if (!context?.userPreferences?.bundleChipIdentityEnabled) return true;
    const scope = this.containmentScope();
    if (!scope) return true;
    const current = this.connectionGraph.getAllConnections();
    const after = analyzeBundleCompatibility(
      scope.manager,
      [...current, connection],
      scope.instances,
    );
    // A new edge may not participate in any incompatible component. A preexisting
    // A/B conflict elsewhere cannot authorize another A/B connection.
    if (after.budgetExceeded || after.blocked.includes(connection)) {
      context.ui?.warnConnection?.(
        after.budgetExceeded
          ? BUNDLE_COMPATIBILITY_BUDGET_MESSAGE
          : BUNDLE_COMPATIBILITY_MESSAGE,
      );
      return false;
    }
    return true;
  }

  connectionFlags(tileId: TileId): Record<Side, boolean> {
    return this.compatibleGraph.getConnectionFlags(tileId);
  }

  connectionsFor(tileId: TileId): Map<Side, ConnectionPoint> {
    const result = new Map<Side, ConnectionPoint>();
    const connections = this.compatibleGraph.getConnectionsForTile(tileId);

    for (const connection of connections) {
      if (connection.a.tileId === tileId) {
        result.set(connection.a.side, connection.b);
      } else if (connection.b.tileId === tileId) {
        result.set(connection.b.side, connection.a);
      }
    }

    return result;
  }

  connectedNeighbors(tileId: TileId): ConnectedNeighbor[] {
    return this.compatibleGraph.getConnectedNeighbors(tileId);
  }

  connect(
    pointA: ConnectionPoint,
    pointB: ConnectionPoint,
    options?: ConnectionModelConnectOptions,
  ): void {
    if (!this.preservesFixedScaffold(pointA, pointB)) return;
    if (pointA.tileId === pointB.tileId && pointA.side === pointB.side) {
      return;
    }

    const tileA = this.resolveTile(pointA.tileId, options);
    const tileB = this.resolveTile(pointB.tileId, options);

    if (!tileA || !tileB) {
      return;
    }

    // Validate adjacency: tiles must be neighbors (Manhattan distance 1).
    const dx = tileB.location.x - tileA.location.x;
    const dy = tileB.location.y - tileA.location.y;
    if (Math.abs(dx) + Math.abs(dy) !== 1) {
      return;
    }

    const canConnect = this.connectionCalculator.canConnect(
      tileA,
      pointA.side,
      tileB,
      pointB.side,
      { allowOptional: true },
    );

    if (!canConnect) {
      return;
    }

    if (!options?.transient && !this.acceptBundleCompatibility({ a: pointA, b: pointB }))
      return;

    if (!options?.transient && this.pendingConnectionBatch) {
      this.pendingConnectionBatch.push({ a: { ...pointA }, b: { ...pointB }, options });
      return;
    }
    if (
      this.approvedBatchScope &&
      !this.connectionGraph
        .getConnectionsForTile(pointA.tileId)
        .some(
          (edge) =>
            this.makeConnectionKey(edge.a, edge.b) ===
            this.makeConnectionKey(pointA, pointB),
        )
    )
      this.rememberMemberConnection({ a: pointA, b: pointB }, this.approvedBatchScope);
    if (
      !options?.transient &&
      !this.approvedBatchScope &&
      !this.acceptProspectiveConnection(
        { a: pointA, b: pointB },
        !options?.tiles && !options?.tileManagers,
      )
    )
      return;
    this.disconnected.delete(this.makeConnectionKey(pointA, pointB));
    this.connectionGraph.addConnection(
      { tileId: pointA.tileId, side: pointA.side },
      { tileId: pointB.tileId, side: pointB.side },
    );

    if (!options?.transient) {
      this.manualConnections.set(this.makeConnectionKey(pointA, pointB), {
        a: { tileId: pointA.tileId, side: pointA.side },
        b: { tileId: pointB.tileId, side: pointB.side },
      });
    }
    this.syncTileConnectionVersions([pointA.tileId, pointB.tileId]);
  }

  /** Add a connection directly, bypassing geometric validation. */
  addConnection(pointA: ConnectionPoint, pointB: ConnectionPoint): void {
    if (!this.preservesFixedScaffold(pointA, pointB)) return;
    if (!this.acceptBundleCompatibility({ a: pointA, b: pointB })) return;
    if (!this.acceptProspectiveConnection({ a: pointA, b: pointB })) return;
    this.disconnected.delete(this.makeConnectionKey(pointA, pointB));
    this.connectionGraph.addConnection(pointA, pointB);
    this.syncTileConnectionVersions([pointA.tileId, pointB.tileId]);
  }

  isFixedConnection(a: ConnectionPoint, b: ConnectionPoint) {
    const tileA = this.getTileById(a.tileId),
      tileB = this.getTileById(b.tileId);
    return !!tileA && !!tileB && isFixedTile(tileA) && isFixedTile(tileB);
  }

  private preservesFixedScaffold(a: ConnectionPoint, b: ConnectionPoint) {
    if (!this.isFixedConnection(a, b)) return true;
    const neighbor = this.connectionsFor(a.tileId).get(a.side);
    return neighbor?.tileId === b.tileId && neighbor.side === b.side;
  }

  disconnect(
    pointA: ConnectionPoint,
    pointB: ConnectionPoint,
    options?: { preventReconnect?: boolean },
  ): void {
    if (this.isFixedConnection(pointA, pointB)) return;
    const key = this.makeConnectionKey(pointA, pointB);
    if (options?.preventReconnect) {
      this.disconnected.set(key, { a: { ...pointA }, b: { ...pointB } });
    } else if (options?.preventReconnect === false) {
      // Explicitly clear the old local-frame suppression before transforming it.
      this.disconnected.delete(key);
    }
    const manualConnection = this.manualConnections.get(key);

    if (manualConnection) {
      this.manualConnections.delete(key);
      this.connectionGraph.removeConnection(manualConnection);
      this.syncTileConnectionVersions([pointA.tileId, pointB.tileId]);
      return;
    }

    const connections = this.connectionGraph.getConnectionsForTile(pointA.tileId);
    for (const connection of connections) {
      if (this.makeConnectionKey(connection.a, connection.b) === key) {
        this.connectionGraph.removeConnection(connection);
        this.syncTileConnectionVersions([connection.a.tileId, connection.b.tileId]);
        break;
      }
    }
  }

  removeConnectionsForTile(tileId: TileId): void {
    this.removeConnectionsForTiles([tileId]);
  }

  /** Retire a preview batch with one ordered graph compaction and manual-edge scan. */
  removeConnectionsForTiles(tileIds: Iterable<TileId>): void {
    const removedTileIds = new Set(tileIds);
    if (!removedTileIds.size) return;
    const affectedTileIds = new Set(removedTileIds);
    for (const tileId of removedTileIds)
      for (const connection of this.connectionGraph.getConnectionsForTile(tileId)) {
        affectedTileIds.add(connection.a.tileId);
        affectedTileIds.add(connection.b.tileId);
      }

    this.connectionGraph.removeConnectionsForTiles(removedTileIds);
    const keysToDelete: string[] = [];
    for (const [key, connection] of this.manualConnections) {
      if (
        removedTileIds.has(connection.a.tileId) ||
        removedTileIds.has(connection.b.tileId)
      ) {
        keysToDelete.push(key);
      }
    }
    for (const key of keysToDelete) {
      this.manualConnections.delete(key);
    }
    this.syncTileConnectionVersions(affectedTileIds, removedTileIds);
  }

  breakOptionalConnections(tileId: TileId): void {
    const connections = this.connectionGraph.getConnectionsForTile(tileId);
    if (connections.length === 0) {
      return;
    }

    const tile = this.getTileById(tileId);
    if (!tile) {
      return;
    }

    const toRemove: Connection[] = [];

    for (const connection of connections) {
      const ownPoint = connection.a.tileId === tileId ? connection.a : connection.b;
      const neighborPoint = connection.a.tileId === tileId ? connection.b : connection.a;

      const neighborTile = this.getTileById(neighborPoint.tileId);
      if (!neighborTile) {
        continue;
      }

      const isForced = this.connectionCalculator.isConnectionForced(
        tile,
        ownPoint.side,
        neighborTile,
        neighborPoint.side,
      );

      if (!isForced) {
        toRemove.push(connection);
      }
    }

    if (toRemove.length === 0) {
      return;
    }

    for (const connection of toRemove) {
      this.manualConnections.delete(this.makeConnectionKey(connection.a, connection.b));
      this.connectionGraph.removeConnection(connection);
    }

    // Force recalculation to ensure UI updates and connections are consistent
    this.recalculateTile(tileId);
  }

  recalculateTile(tileId: TileId): void {
    this.recalculateTiles([tileId]);
  }

  /** Reconcile restored interface settings in one pass before building chip graphs. */
  recalculateTiles(tileIds: Iterable<TileId>): void {
    const previousEdges = [...this.connectionGraph.getAllConnections()];
    const recalculated = this.connectionCalculator.recalculateConnectionsForTiles(
      tileIds,
      this.options.tileManagerModel,
      this.connectionGraph,
    );
    this.reapplyManualConnections(recalculated);
    this.applyDisconnections(recalculated);
    this.rejectExpandingConnections(previousEdges);
    this.syncTileConnectionVersions(recalculated);
  }

  get dto(): ConnectionGraphDto {
    // Read graphVersion to make MobX track this dependency and invalidate
    // the computed when connections change (ConnectionGraph is not observable)
    void this.graphVersion;
    // Observable map values are proxies. Detach both endpoints before native
    // worker/IndexedDB cloning, and keep the DTO independent of later edits.
    const disconnected = [...this.disconnected.values()]
      .filter(
        (edge) =>
          this.tileByIdIndex.has(edge.a.tileId) && this.tileByIdIndex.has(edge.b.tileId),
      )
      .map(({ a, b }) => ({ a: { ...a }, b: { ...b } }));
    return {
      ...this.connectionGraph.dto,
      ...(disconnected.length ? { disconnected } : {}),
    };
  }

  private applyDisconnections(affected?: ReadonlySet<TileId>): void {
    for (const [key, edge] of this.disconnected) {
      if (affected && !affected.has(edge.a.tileId) && !affected.has(edge.b.tileId))
        continue;
      this.manualConnections.delete(key);
      this.connectionGraph.removeConnection(edge);
    }
  }

  private resolveTile(
    tileId: TileId,
    options?: ConnectionModelConnectOptions,
  ): TileInstance | undefined {
    if (options?.tiles) {
      const directMatch = options.tiles.find((tile) => tile.id === tileId);
      if (directMatch) {
        return directMatch;
      }
    }

    if (options?.tileManagers) {
      for (const manager of options.tileManagers) {
        const match = manager.tiles.find((tile) => tile.id === tileId);
        if (match) {
          return match;
        }
      }
    }

    // Use indexed lookup (O(1)) instead of linear search
    return this.getTileById(tileId);
  }

  private getTileById(tileId: TileId): TileInstance | undefined {
    const indexed = this.tileByIdIndex.get(tileId);
    if (indexed) {
      return indexed;
    }
    // Fallback to linear search if index is stale (e.g. tile just added before reaction fired)
    const found = this.options.tileManagerModel.tiles.find((tile) => tile.id === tileId);
    if (found) {
      this.tileByIdIndex.set(found.id, found);
    }
    return found;
  }

  private rebuildTileIndex(): void {
    this.tileByIdIndex.clear();
    for (const tile of this.options.tileManagerModel.tiles) {
      this.tileByIdIndex.set(tile.id, tile);
    }
  }

  private seedManualConnectionsFromGraph(): void {
    for (const connection of this.connectionGraph.getAllConnections()) {
      this.manualConnections.set(
        this.makeConnectionKey(connection.a, connection.b),
        connection,
      );
    }
  }

  private reapplyManualConnections(affected?: ReadonlySet<TileId>): void {
    if (this.manualConnections.size === 0) {
      return;
    }

    const keysToDelete: string[] = [];
    for (const [key, connection] of this.manualConnections) {
      if (
        affected &&
        !affected.has(connection.a.tileId) &&
        !affected.has(connection.b.tileId)
      )
        continue;
      const tileA = this.getTileById(connection.a.tileId);
      const tileB = this.getTileById(connection.b.tileId);

      if (!tileA || !tileB) {
        keysToDelete.push(key);
        continue;
      }

      const canConnect = this.connectionCalculator.canConnect(
        tileA,
        connection.a.side,
        tileB,
        connection.b.side,
        { allowOptional: true },
      );

      if (!canConnect) {
        keysToDelete.push(key);
        continue;
      }

      this.connectionGraph.addConnection(connection.a, connection.b);
    }

    for (const key of keysToDelete) {
      this.manualConnections.delete(key);
    }
  }

  private containmentOwners(tiles: TileInstance[]): ReadonlySet<string | null> {
    if (this.containmentOwnerCacheDisabled) return collectContainmentOwners(tiles);
    if (!this.containmentOwnerCache) {
      const manager = this.options.tileManagerModel;
      if (
        !isConstructedTileCollection(manager, tiles) ||
        !isObservableProp(manager, 'tiles') ||
        isComputedProp(manager, 'tiles') ||
        !isObservableArray(tiles)
      ) {
        this.containmentOwnerCacheDisabled = true;
        return collectContainmentOwners(tiles);
      }
      const released = observable.box(false);
      const invalid = Symbol('untracked owner');
      // Membership edits must rebuild the owner set, but unchanged native tiles
      // keep their observable owner/proof. Rechecking every MobX descriptor for
      // thousands of tiles dominated admission of one adjacent gate.
      const members = new WeakMap<
        TileInstance,
        IComputedValue<string | null | typeof invalid>
      >();
      const owners = computed(
        () => {
          if (released.get()) return undefined;
          const currentTiles = manager.tiles;
          if (
            !isConstructedTileCollection(manager, currentTiles) ||
            !isObservableArray(currentTiles)
          )
            return undefined;
          const result = new Set<string | null>();
          for (const tile of currentTiles) {
            let member = members.get(tile);
            if (!member) {
              member = computed(() => {
                // Unknown/custom sources retain the complete fresh-read path.
                if (
                  !isConstructedTileInstance(tile) ||
                  Object.getPrototypeOf(tile) !== TileInstance.prototype ||
                  !isConstructedTileMeta(tile.meta) ||
                  !isObservableProp(tile, 'meta') ||
                  isComputedProp(tile, 'meta') ||
                  !isObservableProp(tile.meta, 'partOfChip') ||
                  isComputedProp(tile.meta, 'partOfChip')
                )
                  return invalid;
                return tile.meta.partOfChip;
              });
              members.set(tile, member);
            }
            const owner = member.get();
            if (owner === invalid) return undefined;
            if (owner) result.add(owner);
          }
          return result;
        },
        { keepAlive: true },
      );
      this.containmentOwnerCache = owners;
      // Retain the computed without eagerly scanning all owners after every edit.
      // Admission reads refresh it synchronously, including inside an action;
      // removals and isolated edits need no scan when no new edge is admitted.
      this.containmentOwnerDisposer = () =>
        runInAction(() => {
          released.set(true);
          // Re-evaluate once to release every retained tile/collection dependency.
          void owners.get();
        });
    }
    const owners = this.containmentOwnerCache.get();
    if (owners !== undefined) return owners;
    this.containmentOwnerDisposer?.();
    this.containmentOwnerDisposer = undefined;
    this.containmentOwnerCache = undefined;
    this.containmentOwnerCacheDisabled = true;
    return collectContainmentOwners(tiles);
  }

  private containmentContext() {
    const context = this.tileByIdIndex.values().next().value?.context;
    if (
      !context?.project ||
      typeof context.chipStore?.getChip !== 'function' ||
      context.isHydrating ||
      context.editor?.placeTileModel?.previewConnectionModel === this
    )
      return undefined;
    return context;
  }

  private containmentScope(context = this.containmentContext()) {
    if (!context) return undefined;
    const tiles = this.options.tileManagerModel.tiles;
    const instances: IChipInstanceModel[] = [];
    const currentCircuit = context.editor?.currentCircuit;
    let authoredInstances =
      currentCircuit?.tileManagerModel === this.options.tileManagerModel
        ? currentCircuit.chips
        : undefined;
    if (!authoredInstances) {
      for (const chip of context.chipStore?.chips ?? []) {
        const circuit = [
          chip.logicCircuit?.circuitModel,
          chip.structureCircuit?.circuitModel,
        ].find((circuit) => circuit?.tileManagerModel === this.options.tileManagerModel);
        if (circuit) {
          authoredInstances = circuit.chips;
          break;
        }
      }
    }
    const byOwner = new Map(
      (authoredInstances ?? []).map((instance) => [instance.id as string, instance]),
    );
    const owners = this.containmentOwners(tiles);
    for (const owner of owners) {
      const instance =
        byOwner.get(owner!) ??
        context.project.chipInstanceMap?.get(owner! as ChipInstanceId);
      if (instance) instances.push(instance);
    }
    if (!instances.length) return undefined;
    // Reuse the snapshot during candidate admission; never re-read the manager
    // for every tentative edge or expose runtime graphs to the pure analyzer.
    const manager = new Proxy(this.options.tileManagerModel, {
      get: (target, key) => (key === 'tiles' ? tiles : Reflect.get(target, key)),
    });
    return { context, manager, instances };
  }

  private isMemberConnection(
    edge: Connection,
    scope: NonNullable<ReturnType<ConnectionModel['containmentScope']>>,
  ) {
    return [edge.a, edge.b].some((point) => {
      const tile = this.getTileById(point.tileId);
      return (
        tile?.tileType === TileType.SingleInterface &&
        scope.instances.some(
          (instance) =>
            instance.id === tile.meta.partOfChip &&
            instance.chip?.kind === 'bundle-adapter',
        )
      );
    });
  }

  private rememberMemberConnection(
    edge: Connection,
    knownScope?: NonNullable<ReturnType<ConnectionModel['containmentScope']>>,
  ) {
    const scope = knownScope ?? this.containmentScope();
    const ui = scope?.context.ui;
    if (!scope || !ui?.connectionGestureActive || !this.isMemberConnection(edge, scope))
      return;
    this.recentMemberConnections.set(this.makeConnectionKey(edge.a, edge.b), {
      edge,
      gesture: ui.connectionGestureSequence ?? 0,
    });
  }

  private retractGestureMember(
    current: readonly Connection[],
    edge: Connection,
    scope: NonNullable<ReturnType<ConnectionModel['containmentScope']>>,
  ): Connection | undefined {
    const ui = scope.context.ui;
    if (!ui?.connectionGestureActive || this.isMemberConnection(edge, scope))
      return undefined;
    const candidates = [...this.recentMemberConnections.values()].reverse();
    const keys = new Set(current.map((item) => this.makeConnectionKey(item.a, item.b)));
    for (const item of candidates) {
      const key = this.makeConnectionKey(item.edge.a, item.edge.b);
      if (item.gesture !== ui.connectionGestureSequence || !keys.has(key)) continue;
      const without = current.filter(
        (item) => this.makeConnectionKey(item.a, item.b) !== key,
      );
      const before = analyzeProspectiveContainment(
        scope.manager,
        without,
        scope.instances,
      );
      const after = analyzeProspectiveContainment(
        scope.manager,
        [...without, edge],
        scope.instances,
      );
      if (
        !after.budgetExceeded &&
        [...after.cyclicRelationKeys].every((relation) =>
          before.cyclicRelationKeys.has(relation),
        )
      ) {
        this.blockContainmentConnection(item.edge, scope);
        this.recentMemberConnections.delete(key);
        this.syncTileConnectionVersions([item.edge.a.tileId, item.edge.b.tileId]);
        return item.edge;
      }
    }
    return undefined;
  }

  private blockContainmentConnection(
    connection: Connection,
    scope: NonNullable<ReturnType<ConnectionModel['containmentScope']>>,
    budgetExceeded = false,
  ) {
    const key = this.makeConnectionKey(connection.a, connection.b);
    this.disconnected.set(key, { a: { ...connection.a }, b: { ...connection.b } });
    this.manualConnections.delete(key);
    this.connectionGraph.removeConnection(connection);
    scope.context.ui?.warnConnection?.(
      budgetExceeded
        ? 'Connection blocked: this circuit exceeds the bundle topology limit.'
        : 'Connection blocked: a bundle cannot contain itself.',
    );
  }

  /**
   * A standalone passive Wire with one attachment in the complete proposal is
   * a new leaf in authored containment. It cannot merge old port classes or
   * create a packing cycle, even when the old graph cannot fit the scan budget.
   * This certificate is recomputed from current authored data, never cached or
   * inferred from a budget-aborted analysis or from lowered runtime aliases.
   */
  private pendantConnectionCertificate(
    tiles: readonly TileInstance[],
    current: readonly Connection[],
    candidates: readonly Connection[],
  ) {
    if (!candidates.length) return undefined;
    const endpoints = new Map<TileId, TileInstance | null | undefined>();
    for (const { a, b } of candidates) {
      endpoints.set(a.tileId, undefined);
      endpoints.set(b.tileId, undefined);
    }
    for (const tile of tiles) {
      if (!endpoints.has(tile.id)) continue;
      endpoints.set(tile.id, endpoints.get(tile.id) === undefined ? tile : null);
    }
    const degrees = new Map<TileId, number>();
    for (const [id, tile] of endpoints)
      if (
        tile?.tileType === TileType.Wire &&
        tile.state === null &&
        !tile.meta.partOfChip &&
        !tile.meta.bundle &&
        !tile.meta.cable &&
        !tile.adapterPort &&
        !tile.adapterPin &&
        !tile.cablePort &&
        !tile.previewInterfaceLink &&
        !tile.adapterSignalIds?.length &&
        !Object.keys(tile.adapterSignalLabels ?? {}).length &&
        !Object.keys(tile.laneNodeIds ?? {}).length &&
        !Object.keys(tile.laneSignalIds ?? {}).length &&
        !Object.keys(tile.laneSignalLabels ?? {}).length &&
        !Object.keys(tile.laneVisualFlips ?? {}).length &&
        !Object.keys(tile.laneInputs ?? {}).length &&
        !Object.keys(tile.laneOutputs ?? {}).length &&
        !Object.keys(tile.bundleSignalLabels ?? {}).length
      )
        degrees.set(id, 0);
    // Count every side attachment; two sides to one neighbor are still two
    // attachments. Duplicate proposals conservatively fall back to the scan.
    for (const edges of [current, candidates])
      for (const { a, b } of edges)
        for (const point of [a, b])
          if (degrees.has(point.tileId))
            degrees.set(point.tileId, degrees.get(point.tileId)! + 1);
    const wires = new Set<TileId>();
    for (const { a, b } of candidates) {
      const tileA = endpoints.get(a.tileId),
        tileB = endpoints.get(b.tileId);
      if (!tileA || !tileB) return undefined;
      const facing = resolveConnectionSides(tileA, tileB);
      if (
        !facing ||
        facing.sideA !== a.side ||
        facing.sideB !== b.side ||
        !this.connectionCalculator.canConnect(tileA, a.side, tileB, b.side, {
          allowOptional: true,
        })
      )
        return undefined;
      const wire =
        degrees.get(a.tileId) === 1
          ? a.tileId
          : degrees.get(b.tileId) === 1
            ? b.tileId
            : undefined;
      if (!wire) return undefined;
      wires.add(wire);
    }
    return { kind: 'pendant-wires' as const, wires };
  }

  private acceptProspectiveConnection(
    connection: Connection,
    allowPendant = true,
  ): boolean {
    const key = this.makeConnectionKey(connection.a, connection.b);
    if (
      this.connectionGraph
        .getConnectionsForTile(connection.a.tileId)
        .some((edge) => this.makeConnectionKey(edge.a, edge.b) === key)
    )
      return true;
    const current = this.connectionGraph.getAllConnections();
    const scope = this.containmentScope();
    if (!scope) return true;
    if (
      allowPendant &&
      this.pendantConnectionCertificate(scope.manager.tiles, current, [connection])
    ) {
      this.rememberMemberConnection(connection, scope);
      return true;
    }
    const after = analyzeProspectiveContainment(
      scope.manager,
      [...current, connection],
      scope.instances,
    );
    // A failed budget check cannot admit the edge, regardless of the baseline.
    // A complete result without cycles likewise needs no baseline comparison.
    const before =
      !after.budgetExceeded && after.cyclicRelationKeys.size
        ? analyzeProspectiveContainment(scope.manager, current, scope.instances)
        : undefined;
    if (
      after.budgetExceeded ||
      [...after.cyclicRelationKeys].some(
        (relation) => !before?.cyclicRelationKeys.has(relation),
      )
    ) {
      if (this.retractGestureMember(current, connection, scope)) return true;
      this.blockContainmentConnection(connection, scope, after.budgetExceeded);
      this.syncTileConnectionVersions([connection.a.tileId, connection.b.tileId]);
      return false;
    }
    this.rememberMemberConnection(connection, scope);
    return true;
  }

  private rejectExpandingConnections(previousEdges: readonly Connection[]): void {
    // Preview/private circuits have no containment scope; keep their fast exit
    // before enumerating the graph as well as before collecting tile owners.
    const context = this.containmentContext();
    if (!context) return;
    const previousKeys = new Set(
      previousEdges.map((edge) => this.makeConnectionKey(edge.a, edge.b)),
    );
    const proposed = [...this.connectionGraph.getAllConnections()];
    const candidates = proposed.filter(
      (edge) => !previousKeys.has(this.makeConnectionKey(edge.a, edge.b)),
    );
    // This guard only rejects newly introduced edges. Existing saved edges
    // remain intact even when their circuit is already over budget or cyclic.
    if (!candidates.length) return;
    const scope = this.containmentScope(context);
    if (!scope) return;
    const accepted = proposed.filter((edge) =>
      previousKeys.has(this.makeConnectionKey(edge.a, edge.b)),
    );
    if (scope.context.userPreferences?.bundleChipIdentityEnabled) {
      for (const edge of [...candidates]) {
        const result = analyzeBundleCompatibility(
          scope.manager,
          [...accepted, edge],
          scope.instances,
        );
        if (result.budgetExceeded || result.blocked.includes(edge)) {
          const key = this.makeConnectionKey(edge.a, edge.b);
          this.disconnected.set(key, { a: { ...edge.a }, b: { ...edge.b } });
          this.manualConnections.delete(key);
          this.connectionGraph.removeConnection(edge);
          scope.context.ui?.warnConnection?.(
            result.budgetExceeded
              ? BUNDLE_COMPATIBILITY_BUDGET_MESSAGE
              : BUNDLE_COMPATIBILITY_MESSAGE,
          );
          candidates.splice(candidates.indexOf(edge), 1);
          proposed.splice(proposed.indexOf(edge), 1);
        } else accepted.push(edge);
      }
      // The containment pass below must still compare against only old edges.
      accepted.splice(
        0,
        accepted.length,
        ...proposed.filter((edge) =>
          previousKeys.has(this.makeConnectionKey(edge.a, edge.b)),
        ),
      );
    }
    if (this.pendantConnectionCertificate(scope.manager.tiles, accepted, candidates)) {
      for (const edge of candidates) this.rememberMemberConnection(edge, scope);
      return;
    }
    let baseline: ReturnType<typeof analyzeProspectiveContainment> | undefined;
    const baselineKeys = () =>
      (baseline ??= analyzeProspectiveContainment(
        scope.manager,
        accepted,
        scope.instances,
      )).cyclicRelationKeys;
    const complete = analyzeProspectiveContainment(
      scope.manager,
      proposed,
      scope.instances,
    );
    if (
      !complete.budgetExceeded &&
      [...complete.cyclicRelationKeys].every((relation) => baselineKeys().has(relation))
    ) {
      // Healthy large pastes/drag batches need one topology traversal, rather
      // than rebuilding the same union/SCC analysis for every new wire edge.
      if (scope.context.ui?.connectionGestureActive)
        for (const edge of proposed)
          if (!previousKeys.has(this.makeConnectionKey(edge.a, edge.b)))
            this.rememberMemberConnection(edge, scope);
      return;
    }
    candidates.sort(
      (a, b) =>
        Number(this.isMemberConnection(a, scope)) -
        Number(this.isMemberConnection(b, scope)),
    );
    for (const edge of candidates) {
      if (previousKeys.has(this.makeConnectionKey(edge.a, edge.b))) continue;
      const candidate = analyzeProspectiveContainment(
        scope.manager,
        [...accepted, edge],
        scope.instances,
      );
      if (
        candidate.budgetExceeded ||
        [...candidate.cyclicRelationKeys].some(
          (relation) => !baselineKeys().has(relation),
        )
      ) {
        const removed = this.retractGestureMember(accepted, edge, scope);
        if (removed) {
          const index = accepted.findIndex(
            (item) =>
              this.makeConnectionKey(item.a, item.b) ===
              this.makeConnectionKey(removed.a, removed.b),
          );
          if (index >= 0) accepted.splice(index, 1);
          accepted.push(edge);
          baseline = undefined;
        } else this.blockContainmentConnection(edge, scope, candidate.budgetExceeded);
      } else {
        accepted.push(edge);
        baseline = candidate;
        this.rememberMemberConnection(edge, scope);
      }
    }
  }

  private makeConnectionKey(a: ConnectionPoint, b: ConnectionPoint): string {
    const keyA = `${a.tileId}:${a.side}`;
    const keyB = `${b.tileId}:${b.side}`;
    return keyA < keyB ? `${keyA}|${keyB}` : `${keyB}|${keyA}`;
  }

  private syncTileConnectionVersions(
    tileIds?: Iterable<TileId>,
    removedTileIds?: Iterable<TileId>,
  ): void {
    this.graphVersion++;
    const removed = removedTileIds ? [...removedTileIds] : [];
    if (removedTileIds) {
      for (const removedTileId of removed) {
        this.tileConnectionFlagsSnapshotMap.delete(removedTileId);
        this.tileConnectionVersionMap.delete(removedTileId);
        if (!this.tileByIdIndex.has(removedTileId))
          this.connectionGraph.forgetUnusedTileIndexes(removedTileId);
      }
    }

    const idsToSync = tileIds
      ? Array.from(tileIds)
      : this.options.tileManagerModel.tiles.map((tile) => tile.id);
    // Endpoint identities matter even when the four connected-side flags are
    // unchanged (for example a manual link retargeted to another distant tile).
    this.changes.record(
      this.graphVersion,
      tileIds ? [...idsToSync, ...removed] : undefined,
    );

    for (const tileId of idsToSync) {
      if (!this.tileByIdIndex.has(tileId)) continue;
      const flags = this.connectionGraph.getConnectionFlags(tileId);
      const nextSnapshot = `${Number(flags[Side.TOP])}${Number(flags[Side.RIGHT])}${Number(flags[Side.BOTTOM])}${Number(flags[Side.LEFT])}`;
      const previousSnapshot = this.tileConnectionFlagsSnapshotMap.get(tileId);

      if (previousSnapshot === nextSnapshot) {
        continue;
      }

      this.tileConnectionFlagsSnapshotMap.set(tileId, nextSnapshot);
      this.tileConnectionVersionMap.set(
        tileId,
        (this.tileConnectionVersionMap.get(tileId) ?? 0) + 1,
      );
    }
  }

  private recalculateConnectionsForTileDelta(
    affectedTileIds: Set<TileId>,
    removedTileIds: Set<TileId>,
  ): Set<TileId> {
    if (affectedTileIds.size === 0 && removedTileIds.size === 0) {
      return affectedTileIds;
    }
    const previousEdges = [...this.connectionGraph.getAllConnections()];

    const recalculated = this.connectionCalculator.recalculateConnectionsForTiles(
      new Set([...affectedTileIds, ...removedTileIds]),
      this.options.tileManagerModel,
      this.connectionGraph,
      this.tileTracker?.incremental ? this.tileTracker : undefined,
    );

    this.reapplyManualConnections(recalculated);
    this.applyDisconnections(recalculated);
    this.rejectExpandingConnections(previousEdges);
    return recalculated;
  }
}

// Captured in the defining module, before callers can replace prototype hooks.
const canonicalConnectionFlags = Object.getOwnPropertyDescriptor(
  ConnectionModel.prototype,
  'connectionFlags',
)!.value;
const canonicalConnectionVersion = Object.getOwnPropertyDescriptor(
  ConnectionModel.prototype,
  'connectionVersionForTile',
)!.value;
const nativeConnectionSelectors = new WeakMap<
  object,
  { flags: unknown; version: unknown }
>();

function hasNativeConnectionSelectorPrototype(value: object): boolean {
  return (
    Object.getPrototypeOf(value) === ConnectionModel.prototype &&
    Object.getOwnPropertyDescriptor(ConnectionModel.prototype, 'connectionFlags')
      ?.value === canonicalConnectionFlags &&
    Object.getOwnPropertyDescriptor(ConnectionModel.prototype, 'connectionVersionForTile')
      ?.value === canonicalConnectionVersion
  );
}

function protectedSelectorDescriptor(
  value: object,
  key: 'connectionFlags' | 'connectionVersionForTile',
) {
  const descriptor = Object.getOwnPropertyDescriptor(value, key);
  return descriptor &&
    'value' in descriptor &&
    typeof descriptor.value === 'function' &&
    descriptor.writable === false &&
    descriptor.configurable === false
    ? descriptor
    : undefined;
}

/** Native public lookup methods only. Proxies, subclasses and custom providers
 * retain per-reaction hook calls; private graph internals are outside this proof.
 */
export function isNativeConnectionSelectorSource(
  value: unknown,
): value is ConnectionModel {
  if (typeof value !== 'object' || value === null) return false;
  const methods = nativeConnectionSelectors.get(value);
  if (!methods || !hasNativeConnectionSelectorPrototype(value)) return false;
  return (
    protectedSelectorDescriptor(value, 'connectionFlags')?.value === methods.flags &&
    protectedSelectorDescriptor(value, 'connectionVersionForTile')?.value ===
      methods.version
  );
}
